import Component from '@glimmer/component'
import { service } from '@ember/service'
import { action } from '@ember/object'
import { tracked } from '@glimmer/tracking'

import { Changeset } from 'ember-changeset'
import lookupValidator from 'teacher-ui-client/utils/lookup-validator'
import GradePositionValidator from 'teacher-ui-client/components/form-wrapper/edit-grade-form/validator'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import type { IntlService } from 'ember-intl'
import type Student from 'teacher-ui-client/models/student'
import type { Task, TaskInstance } from 'ember-concurrency'
import { on } from '@ember/modifier'
import { t } from 'ember-intl'
import { FormField } from 'teacher-ui-client/components/form-field'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import type { BufferedChangeset } from 'ember-changeset/types'
import { FormWrapperSubmitButton } from 'teacher-ui-client/components/form-wrapper/submit-button'
import submitButtonState from 'teacher-ui-client/helpers/submit-button-state'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { or } from 'ember-truth-helpers'
import { optional } from 'teacher-ui-client/helpers/optional'
import type ErrorHandlerService from 'teacher-ui-client/services/error-handler'
import type ErrorHandler from 'teacher-ui-client/utils/error-handler'
import { didCancel } from 'ember-concurrency'
import { assert } from '@ember/debug'
import type Owner from '@ember/owner'

type MultipleStudentTask = Task<void, [Student[], number]>

interface Args {
  students: Student[]
  setMultipleGradesTask: MultipleStudentTask
  onCancel: () => void
}

export class EditGradeForm extends Component<Args> {
  @service gradeSets!: GradeSetsService

  @service intl!: IntlService

  @tracked changeset: BufferedChangeset

  @tracked taskInstance?: TaskInstance<void>

  @service('error-handler') errorHandlerService!: ErrorHandlerService

  @tracked errorHandler!: ErrorHandler

  get gradeOptions() {
    return this.gradeSets.optionsBy('name')
  }

  constructor(owner: Owner, args: Args) {
    super(owner, args)
    const changeset = Changeset(
      { newGrade: '' },
      lookupValidator(GradePositionValidator, { intl: this.intl }),
      GradePositionValidator,
    )
    this.changeset = changeset

    this.errorHandler = this.errorHandlerService.create()
  }

  @action
  onSubmit(event: SubmitEvent) {
    event.preventDefault()
    const newGradePosition = this.changeset.get('newGrade')
    const studentsToUpdate = this.args.students

    assert('new grade position must be a number', typeof newGradePosition === 'number')
    try {
      const setMultipleGradesTaskInstance = this.args.setMultipleGradesTask.perform(studentsToUpdate, newGradePosition)
      this.taskInstance = setMultipleGradesTaskInstance
    } catch (e: any) {
      if (!didCancel(e)) {
        const errors = e?.errors || []
        this.errorHandler.handle(errors)
        this.errorHandler.sendOtherErrorsToLog('Error while updating grade from the "edit grade form" component')
      }
    }
  }

  <template>
    <form {{on "submit" this.onSubmit}}>
      <div class="p-4">
        <p class="mb-3">
          {{t "manage.manageClasses.students.bulkActions.totalSelectedStudents" count=@students.length htmlSafe=true}}
        </p>
        <FormField
          class="max-w-50"
          @changeset={{this.changeset}}
          @propertyName="newGrade"
          @labelText={{t "grade.label"}}
          as |field|
        >
          <field.select
            data-test-select-grade
            @options={{this.gradeOptions}}
            @value={{this.changeset.newGrade}}
            @defaultText={{t "grade.select"}}
            @valueLookupKey="value"
          />
        </FormField>
      </div>
      <div class="border-dusty-black-50 border-t p-2 text-right">
        <UiButton data-test-cancel type="reset" {{on "click" (optional @onCancel)}}>
          {{t "manage.manageClasses.students.bulkActions.cancel"}}
        </UiButton>
        <FormWrapperSubmitButton
          data-test-submit
          @disabled={{or this.changeset.isPristine this.changeset.isInvalid}}
          @defaultText={{t "manage.manageClasses.students.bulkActions.update"}}
          @state={{submitButtonState this.taskInstance this.errorHandler}}
          @appearance={{ButtonAppearance.regular}}
        />
      </div>
    </form>
  </template>
}

export default EditGradeForm
